import React from 'react'
import { Link } from 'react-router-dom'
import { Briefcase, MapPin, Clock, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'
import { Button } from '../components/ui/Button'

export const CareersPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-6">
              <Briefcase className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              Join Our Team
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Help us revolutionize 1031 exchanges. We're building the future of real estate investing.
            </p>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 text-center mb-12">
            Open Positions
          </h2>
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Job Listing 1 */}
            <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                    Senior Full-Stack Engineer
                  </h3>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                    <span className="flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      Remote / San Francisco
                    </span>
                    <span className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      Full-time
                    </span>
                  </div>
                </div>
                <Link to="/contact">
                  <Button className="bg-[#69adba] hover:bg-[#5a9aa6] text-white">
                    Apply Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <p className="text-slate-600 dark:text-slate-400">
                We're looking for an experienced full-stack engineer to help build our next-generation 1031 exchange platform.
              </p>
            </div>

            {/* Job Listing 2 */}
            <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                    Tax Compliance Specialist
                  </h3>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                    <span className="flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      Remote / New York
                    </span>
                    <span className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      Full-time
                    </span>
                  </div>
                </div>
                <Link to="/contact">
                  <Button className="bg-[#69adba] hover:bg-[#5a9aa6] text-white">
                    Apply Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <p className="text-slate-600 dark:text-slate-400">
                Join our compliance team to ensure all exchanges meet IRS regulations and requirements.
              </p>
            </div>

            {/* Job Listing 3 */}
            <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                    Customer Success Manager
                  </h3>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                    <span className="flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      Remote
                    </span>
                    <span className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      Full-time
                    </span>
                  </div>
                </div>
                <Link to="/contact">
                  <Button className="bg-[#69adba] hover:bg-[#5a9aa6] text-white">
                    Apply Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <p className="text-slate-600 dark:text-slate-400">
                Help our customers succeed with their 1031 exchanges through exceptional support and guidance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-slate-50 dark:bg-slate-800/50 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 text-center mb-12">
              Why Work at Open1031?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Competitive Compensation
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  We offer competitive salaries, equity, and comprehensive benefits packages.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Remote-Friendly
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Work from anywhere with flexible hours and a supportive remote culture.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Growth Opportunities
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Advance your career with learning opportunities and mentorship programs.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Impactful Work
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Make a real difference in how people manage their real estate investments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#69adba] dark:bg-[#5a9aa6]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4">
            Don't See a Role That Fits?
          </h2>
          <p className="mt-4 text-lg text-white/90 mb-8">
            We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#69adba] hover:bg-slate-50">
              Get in Touch
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

